import React, { useState, useMemo, useCallback, useRef, useEffect  } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
  Tooltip,
  Grid,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  InputAdornment,
} from '@mui/material';

import { DataGridPro, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarDensitySelector } from '@mui/x-data-grid-pro';
import ResponsiveDataView from '../components/ResponsiveDataView';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { heIL } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useUser } from '../contexts/UserContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Badge } from '@mui/material';

// Import icons
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PrintIcon from '@mui/icons-material/Print';
import HomeIcon from '@mui/icons-material/Home';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import VisibilityIcon from '@mui/icons-material/Visibility';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LabelIcon from '@mui/icons-material/Label';
import MobileDialog from './dialogs/MobileDialog';
import ResponsiveToolbar from '../components/ResponsiveToolbar';
import { useIsPhone } from '../hooks/useIsMobile';
import InventoryManagementDialog from './dialogs/InventoryManagementDialog';
import KioskTokensDialog from './dialogs/KioskTokensDialog';
import BarcodePrintDialog from './dialogs/BarcodePrintDialog';
import GenerateBarcodeDialog from './dialogs/GenerateBarcodeDialog';
import QuickScanDialog from './dialogs/QuickScanDialog';
import BarcodeScannerDialog from './dialogs/BarcodeScannerDialog';
import ProductInsightsPanel from './components/ProductInsightsPanel';
import InventoryFilterPanel from '../components/InventoryFilterPanel';

// Theme setup
const theme = createTheme({
  direction: 'rtl',
  ...heIL,
  typography: {
    fontSize: 16,
  },
});

// Custom toolbar component
function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
    </GridToolbarContainer>
  );
}

// Fetch functions
const fetchPaginatedProducts = async ({ page = 1, pageSize = 25, search = '', ordering = 'ProductName', storageId = '', inventoryMin = null, ids = null }) => {
  const params = new URLSearchParams({
    page: page,
    page_size: pageSize,
    ordering: ordering,
  });

  if (search) {
    params.append('search', search);
  }

  if (storageId) {
    params.append('storage_id', storageId);
  }

  if (inventoryMin != null) {
    params.append('inventory_min', inventoryMin);
  }

  if (Array.isArray(ids) && ids.length > 0) {
    params.append('ids', ids.join(','));
  }

  const response = await API.get(`/products/list/?${params.toString()}`);
  return response.data;
};

const fetchAllProductsForAutocomplete = async () => {
  const response = await API.get('/products/');
  return response.data;
};

const fetchStorages = async () => {
  const response = await API.get('/storages/?include_inventory_stats=true');
  return response.data;
};

// Storage Management Dialog
const StorageManagementDialog = React.memo(({ 
  open, 
  onClose, 
  storage,
  mode,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    StorageName: '',
    Description: '',
    Address: '',
    is_default: false,
  });
  const [formError, setFormError] = useState('');

  React.useEffect(() => {
    if (storage && mode === 'edit') {
      setFormData({
        StorageName: storage.StorageName || '',
        Description: storage.Description || '',
        Address: storage.Address || '',
        is_default: storage.is_default || false,
      });
    } else {
      setFormData({
        StorageName: '',
        Description: '',
        Address: '',
        is_default: false,
      });
    }
  }, [storage, mode]);

  const createStorageMutation = useMutation({
    mutationFn: async (newStorage) => {
      const response = await API.post('/storage/create/', newStorage);
      return response.data;
    },
    onSuccess: () => {
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      setFormError(error?.response?.data?.error || 'שגיאה ביצירת מחסן');
    },
  });

  const updateStorageMutation = useMutation({
    mutationFn: async (updatedStorage) => {
      const response = await API.put('/storage/update/', {
        StorageID: storage.StorageID,
        ...updatedStorage
      });
      return response.data;
    },
    onSuccess: () => {
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      setFormError(error?.response?.data?.error || 'שגיאה בעדכון מחסן');
    },
  });

  const handleClose = () => {
    setFormData({
      StorageName: '',
      Description: '',
      Address: '',
      is_default: false,
    });
    setFormError('');
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      is_default: e.target.checked,
    }));
  };

  const handleSubmit = () => {
    setFormError('');
    
    if (!formData.StorageName.trim()) {
      setFormError('שם מחסן הוא שדה חובה');
      return;
    }
    
    if (mode === 'create') {
      createStorageMutation.mutate(formData);
    } else {
      updateStorageMutation.mutate(formData);
    }
  };

  return (
    <MobileDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'הוסף מחסן חדש' : 'ערוך מחסן'}
      </DialogTitle>
      <DialogContent>
        {formError && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{formError}</Alert>}
        <TextField
          name="StorageName"
          label="שם מחסן"
          fullWidth
          margin="normal"
          value={formData.StorageName}
          onChange={handleInputChange}
          required
        />
        <TextField
          name="Description"
          label="תיאור"
          fullWidth
          margin="normal"
          value={formData.Description}
          onChange={handleInputChange}
          multiline
          rows={2}
        />
        <TextField
          name="Address"
          label="כתובת"
          fullWidth
          margin="normal"
          value={formData.Address}
          onChange={handleInputChange}
        />
        <FormControlLabel
          control={
            <Switch
              checked={formData.is_default}
              onChange={handleSwitchChange}
              name="is_default"
            />
          }
          label="מחסן ברירת מחדל"
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>ביטול</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={createStorageMutation.isLoading || updateStorageMutation.isLoading}
        >
          {(createStorageMutation.isLoading || updateStorageMutation.isLoading) ? 'שומר...' : 'שמור'}
        </Button>
      </DialogActions>
    </MobileDialog>
  );
});

// Delete Storage Dialog
const DeleteStorageDialog = React.memo(({ 
  open, 
  onClose, 
  storage,
  onSuccess
}) => {
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setFormError('');
    setLoading(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (!storage) return;
    
    setFormError('');
    setLoading(true);
    
    try {
      await API.post('/storage/delete/', { 
        StorageID: storage.StorageID,
        force: true 
      });
      onSuccess();
      handleClose();
    } catch (error) {
      setFormError(error?.response?.data?.error || 'שגיאה במחיקת מחסן');
      setLoading(false);
    }
  };

  if (!storage) return null;

  return (
    <MobileDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'error.main' }}>
        אזהרה: מחיקת מחסן
      </DialogTitle>
      <DialogContent>
        {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
        
        <Typography variant="body1" paragraph>
          האם אתה בטוח שברצונך למחוק את המחסן <strong>"{storage.StorageName}"</strong>?
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            שים לב:
          </Typography>
          <Typography variant="body2" component="div">
            • מחיקת המחסן תמחק את כל רישומי המלאי השמורים במחסן זה
            <br />
            • פעולה זו אינה ניתנת לביטול
            {storage.inventory_count > 0 && (
              <>
                <br />
                <br />
                <strong>
                  במחסן זה קיימים {storage.inventory_count} מוצרים עם סה"כ {storage.total_items} פריטים במלאי
                </strong>
              </>
            )}
          </Typography>
        </Alert>
        
        {storage.is_default && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              זהו מחסן ברירת המחדל. לאחר המחיקה יש להגדיר מחסן אחר כברירת מחדל.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          ביטול
        </Button>
        <Button 
          onClick={handleConfirm} 
          color="error" 
          variant="contained"
          disabled={loading}
        >
          {loading ? 'מוחק...' : 'מחק מחסן'}
        </Button>
      </DialogActions>
    </MobileDialog>
  );
});

// InventoryManagementDialog has been extracted to
// frontend/src/pages/dialogs/InventoryManagementDialog.js so the new
// single-page order flow can reuse it. Imported above.

// Create Product Dialog
const CreateProductDialog = React.memo(({
  open,
  onClose,
  productFieldsSchema,  // Schema from UI config (uiConfig.product_fields_schema)
  productLabel,         // Label for product (e.g., 'מוצר')
  onSuccess,           // Callback when product is created successfully
  productFields,       // Array of fields to show (uiConfig.product_fields)
  storageEnabled,      // Whether storage management is enabled
  storages,            // Array of storage objects
  barcodeEnabled = false, // Whether barcode generation is available
}) => {
  // Fields to always exclude from the create form
  const excludedFields = ['CreatedAt', 'IsDeleted', 'DeletedAt', 'ProductID', 'UserGroup'];

  // Step management: 0 = product details, 1 = inventory initialization
  const [activeStep, setActiveStep] = useState(0);

  // Initialize inventory data for each storage
  const [inventoryData, setInventoryData] = useState({});

  // Initialize form data with all editable fields
  const [formData, setFormData] = useState(() => {
    const initialData = {
      ProductName: '',
      Description: '',
      Price: 0,
    };

    // Add all non-readonly and non-excluded fields from schema
    Object.entries(productFieldsSchema).forEach(([fieldName, fieldConfig]) => {
      if (!fieldConfig.readonly &&
          !initialData.hasOwnProperty(fieldName) &&
          productFields.includes(fieldName) &&
          !excludedFields.includes(fieldName)) {
        initialData[fieldName] = fieldConfig.default || '';
      }
    });

    return initialData;
  });

  const [formError, setFormError] = useState('');
  const errorRef = React.useRef(null);

  // Scroll to error message when it appears
  React.useEffect(() => {
    if (formError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [formError]);

  // Check if we should show inventory step
  const shouldShowInventoryStep = storageEnabled && storages && storages.length > 0;

  // Loading state for final submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form and close dialog
  const handleClose = () => {
    // Reset form data
    const resetData = {
      ProductName: '',
      Description: '',
      Price: 0,
    };

    Object.entries(productFieldsSchema).forEach(([fieldName, fieldConfig]) => {
      if (!fieldConfig.readonly &&
          !resetData.hasOwnProperty(fieldName) &&
          productFields.includes(fieldName) &&
          !excludedFields.includes(fieldName)) {
        resetData[fieldName] = fieldConfig.default || '';
      }
    });

    setFormData(resetData);
    setFormError('');
    setActiveStep(0);
    setInventoryData({});
    setIsSubmitting(false);
    onClose();
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validate form data
  const validateForm = () => {
    setFormError('');

    // Always validate ProductName and Price as they're required
    if (!formData.ProductName || formData.ProductName.trim() === '') {
      setFormError(`${productFieldsSchema.ProductName?.label || `שם ${productLabel}`} הוא שדה חובה`);
      return false;
    }

    if (formData.Price === '' || formData.Price === null || formData.Price === undefined || isNaN(parseFloat(formData.Price))) {
      setFormError('יש להזין מחיר תקין');
      return false;
    }

    // Validate other required fields from schema
    const requiredFields = Object.entries(productFieldsSchema)
      .filter(([fieldName, config]) =>
        config.required &&
        productFields.includes(fieldName) &&
        !excludedFields.includes(fieldName) &&
        fieldName !== 'ProductName' &&
        fieldName !== 'Price'
      );

    for (const [fieldName, config] of requiredFields) {
      if (!formData[fieldName] || formData[fieldName].toString().trim() === '') {
        setFormError(`${config.label || fieldName} הוא שדה חובה`);
        return false;
      }
    }

    return true;
  };

  // Prepare form data for submission
  const prepareSubmitData = () => {
    const submitData = {};
    Object.entries(formData).forEach(([key, value]) => {
      // __auto__ barcode: don't send the field — backend auto_generate_on_create handles it
      if (key === 'Barcode' && value === '__auto__') {
        submitData.generate_barcode = true;
        return;
      }
      if (value !== '' && value !== null && value !== undefined) {
        if (key === 'Price' || key === 'CostPrice' || key === 'MinPrice' || productFieldsSchema[key]?.type === 'number') {
          submitData[key] = Math.round(parseFloat(value) * 100) / 100;
        } else {
          submitData[key] = value;
        }
      }
    });
    return submitData;
  };

  // Move to next step or submit directly if no inventory step
  const handleNext = async () => {
    if (!validateForm()) return;

    if (shouldShowInventoryStep) {
      // Initialize inventory data and move to step 1
      const initialInventory = {};
      storages.forEach(storage => {
        initialInventory[storage.StorageID] = '';
      });
      setInventoryData(initialInventory);
      setActiveStep(1);
    } else {
      // No inventory step - create product directly
      await handleFinalSubmit();
    }
  };

  // Go back to product details
  const handleBack = () => {
    setFormError('');
    setActiveStep(0);
  };

  // Final submission - create product and inventory atomically
  const handleFinalSubmit = async () => {
    setFormError('');
    setIsSubmitting(true);

    try {
      const submitData = prepareSubmitData();

      // Add initial inventory data if any quantities > 0
      const initialInventory = {};
      Object.entries(inventoryData).forEach(([storageId, qty]) => {
        if (qty !== '' && parseFloat(qty) > 0) {
          initialInventory[storageId] = parseFloat(qty);
        }
      });

      if (Object.keys(initialInventory).length > 0) {
        submitData.initial_inventory = initialInventory;
      }

      // Single atomic request - creates product and inventory together
      await API.post('/products/create/', submitData);

      onSuccess();
      handleClose();
    } catch (error) {
      setFormError(error?.response?.data?.error || `שגיאה ביצירת ${productLabel}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle inventory quantity change
  const handleInventoryChange = (storageId, value) => {
    setInventoryData(prev => ({
      ...prev,
      [storageId]: value
    }));
  };


  // Render field based on its type and configuration
  const renderField = (fieldName, fieldConfig) => {
    // Skip readonly fields and excluded fields
    if (fieldConfig.readonly || excludedFields.includes(fieldName)) {
      return null;
    }
    
    const fieldType = fieldConfig.type || 'string';
    const label = fieldConfig.label || fieldName;
    const required = fieldConfig.required || false;
    
    // Special handling for Barcode field — add auto-generate toggle
    if (fieldName === 'Barcode') {
      return (
        <Box key={fieldName}>
          <TextField
            name={fieldName}
            label={label}
            fullWidth
            margin="normal"
            value={formData[fieldName] === '__auto__' ? '' : (formData[fieldName] || '')}
            onChange={handleInputChange}
            placeholder={formData[fieldName] === '__auto__' ? 'יוקצה אוטומטית בשמירה' : 'הזן ברקוד ידנית'}
            disabled={formData[fieldName] === '__auto__'}
            InputProps={{ sx: { direction: 'ltr' } }}
          />
          {barcodeEnabled && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData[fieldName] === '__auto__'}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      Barcode: e.target.checked ? '__auto__' : '',
                    }));
                  }}
                  size="small"
                />
              }
              label={<Typography variant="caption">צור ברקוד אוטומטית</Typography>}
              sx={{ mt: -0.5 }}
            />
          )}
        </Box>
      );
    }

    switch (fieldType) {
      case 'number':
        return (
          <TextField
            key={fieldName}
            name={fieldName}
            label={label}
            fullWidth
            margin="normal"
            value={formData[fieldName] || ''}
            onChange={handleInputChange}
            onFocus={(e) => e.target.select()}
            onWheel={(e) => e.target.blur()}
            type="number"
            required={required}
            InputProps={{
              inputProps: {
                min: fieldConfig.min || 0,
                max: fieldConfig.max || undefined,
                step: 0.01
              }
            }}
          />
        );
      
      case 'text':
        return (
          <TextField
            key={fieldName}
            name={fieldName}
            label={label}
            fullWidth
            margin="normal"
            value={formData[fieldName] || ''}
            onChange={handleInputChange}
            multiline
            rows={3}
            required={required}
          />
        );
        
      case 'boolean':
        return (
          <FormControlLabel
            key={fieldName}
            control={
              <Switch
                checked={formData[fieldName] || false}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  [fieldName]: e.target.checked
                }))}
                name={fieldName}
              />
            }
            label={label}
            sx={{ mt: 2, mb: 1 }}
          />
        );
        
      case 'select':
        return (
          <TextField
            key={fieldName}
            name={fieldName}
            label={label}
            fullWidth
            margin="normal"
            value={formData[fieldName] || ''}
            onChange={handleInputChange}
            select
            required={required}
          >
            <MenuItem value="">
              <em>בחר</em>
            </MenuItem>
            {fieldConfig.options?.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );
        
      case 'date':
        return (
          <TextField
            key={fieldName}
            name={fieldName}
            label={label}
            fullWidth
            margin="normal"
            type="date"
            value={formData[fieldName] || ''}
            onChange={handleInputChange}
            required={required}
            InputLabelProps={{
              shrink: true,
            }}
          />
        );
        
      default: // string
        return (
          <TextField
            key={fieldName}
            name={fieldName}
            label={label}
            fullWidth
            margin="normal"
            value={formData[fieldName] || ''}
            onChange={handleInputChange}
            required={required}
          />
        );
    }
  };

  // Define the order of fields to display
  const getFieldsInOrder = () => {
    const orderedFields = [];
      
    // Fields to exclude from form
    const excludedFields = ['CreatedAt', 'IsDeleted', 'DeletedAt', 'ProductID', 'UserGroup'];
    
    // Fields that have specific positions
    const specialOrderFields = [
      'ProductName',
      'CatalogNumber', 
      'Barcode',
      'Price',
      'unit',
      'inventory',
      'totalInventory',
      'Description',
      'actions'
    ];
    
    // 1. Always show ProductName first
    orderedFields.push('ProductName');
    
    // 2. Add CatalogNumber if in product_fields and not readonly
    if (productFields.includes('CatalogNumber') && 
        !excludedFields.includes('CatalogNumber') &&
        (!productFieldsSchema.CatalogNumber || !productFieldsSchema.CatalogNumber.readonly)) {
      orderedFields.push('CatalogNumber');
    }
    
    // 3. Add Barcode if in product_fields and not readonly
    if (productFields.includes('Barcode') && 
        !excludedFields.includes('Barcode') &&
        (!productFieldsSchema.Barcode || !productFieldsSchema.Barcode.readonly)) {
      orderedFields.push('Barcode');
    }
    
    // 4. Always show Price
    orderedFields.push('Price');
    
    // 5. Add unit if in product_fields and not readonly
    if (productFields.includes('unit') && 
        !excludedFields.includes('unit') &&
        (!productFieldsSchema.unit || !productFieldsSchema.unit.readonly)) {
      orderedFields.push('unit');
    }
    
    // 6. Add all other fields from product_fields 
    // (excluding already added, excluded fields, and Description which goes at the end)
    productFields.forEach(fieldName => {
      if (!orderedFields.includes(fieldName) && 
          !excludedFields.includes(fieldName) &&
          !specialOrderFields.includes(fieldName) && // Don't add special order fields here
          productFieldsSchema[fieldName] &&
          !productFieldsSchema[fieldName].readonly) {
        orderedFields.push(fieldName);
      }
    });
    
    // 7. Add Description last (if in product_fields or if no specific fields configured)
    if ((productFields.includes('Description') || productFields.length === 0) && 
        !excludedFields.includes('Description') &&
        (!productFieldsSchema.Description || !productFieldsSchema.Description.readonly)) {
      orderedFields.push('Description');
    }
    
    return orderedFields;
  };

  return (
    <MobileDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {activeStep === 0
          ? `הוסף ${productLabel} חדש`
          : `הגדרת מלאי התחלתי - ${formData.ProductName || ''}`
        }
      </DialogTitle>
      <DialogContent>
        {formError && (
          <Alert ref={errorRef} severity="error" sx={{ mt: 2, mb: 2 }}>
            {formError}
          </Alert>
        )}

        {activeStep === 0 ? (
          <>
            {/* Step 0: Product Details Form */}
            {getFieldsInOrder().map(fieldName => {
              // For core fields (ProductName, Price, Description), render directly
              if (fieldName === 'ProductName') {
                return (
                  <TextField
                    key="ProductName"
                    name="ProductName"
                    label={productFieldsSchema.ProductName?.label || `שם ${productLabel}`}
                    fullWidth
                    margin="normal"
                    value={formData.ProductName}
                    onChange={handleInputChange}
                    required
                    autoFocus
                  />
                );
              }

              if (fieldName === 'Price') {
                return (
                  <TextField
                    key="Price"
                    name="Price"
                    label={productFieldsSchema.Price?.label || "מחיר"}
                    fullWidth
                    margin="normal"
                    value={formData.Price}
                    onChange={handleInputChange}
                    onFocus={(e) => e.target.select()}
                    onWheel={(e) => e.target.blur()}
                    type="number"
                    required
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 }
                    }}
                  />
                );
              }

              if (fieldName === 'Description') {
                return (
                  <TextField
                    key="Description"
                    name="Description"
                    label={productFieldsSchema.Description?.label || "תיאור"}
                    fullWidth
                    margin="normal"
                    value={formData.Description}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                  />
                );
              }

              // For other fields, use the dynamic renderer
              const fieldConfig = productFieldsSchema[fieldName];
              if (fieldConfig) {
                return renderField(fieldName, fieldConfig);
              }

              return null;
            })}
          </>
        ) : (
          <>
            {/* Step 1: Inventory Initialization - Simple Table */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>מחסן</TableCell>
                    <TableCell align="center" sx={{ width: 120 }}>כמות התחלתית</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storages?.map(storage => (
                    <TableRow key={storage.StorageID}>
                      <TableCell>
                        {storage.StorageName}
                        {storage.is_default && (
                          <Chip size="small" label="ברירת מחדל" color="primary" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          value={inventoryData[storage.StorageID] || ''}
                          onChange={(e) => handleInventoryChange(storage.StorageID, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onWheel={(e) => e.target.blur()}
                          inputProps={{ min: 0, step: 1, style: { textAlign: 'center' } }}
                          size="small"
                          placeholder="0"
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {activeStep === 0 ? (
          <>
            <Button onClick={handleClose} disabled={isSubmitting}>ביטול</Button>
            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'שומר...' : (shouldShowInventoryStep ? 'הבא' : 'שמור')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleBack} disabled={isSubmitting}>
              חזור
            </Button>
            <Button
              onClick={handleFinalSubmit}
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'שומר...' : 'סיום'}
            </Button>
          </>
        )}
      </DialogActions>
    </MobileDialog>
  );
});

// Delete Product Dialog
const DeleteProductDialog = React.memo(({ 
  open, 
  onClose, 
  product, 
  productLabel,
  onSuccess
}) => {
  const [formError, setFormError] = useState('');

  const deleteProductMutation = useMutation({
    mutationFn: async (productId) => {
      const response = await API.post('/product/delete/', { ProductID: productId });
      return response.data;
    },
    onSuccess: () => {
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      setFormError(error?.response?.data?.error || `שגיאה במחיקת ${productLabel}`);
    },
  });

  const handleClose = () => {
    setFormError('');
    onClose();
  };

  const handleConfirm = () => {
    if (product) {
      deleteProductMutation.mutate(product.ProductID);
    }
  };

  return (
    <MobileDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>מחיקת {productLabel}</DialogTitle>
      <DialogContent>
        {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
        <Typography>
          האם אתה בטוח שברצונך למחוק את ה{productLabel} "{product?.ProductName}"?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>ביטול</Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={deleteProductMutation.isLoading}
        >
          {deleteProductMutation.isLoading ? 'מוחק...' : 'מחק'}
        </Button>
      </DialogActions>
    </MobileDialog>
  );
});

// Product Autocomplete
const ProductAutocomplete = React.memo(({ 
  products, 
  productLabel, 
  onSearch,
  productFields = [],  // Pass this from parent component
  productFieldsSchema = {}  // Pass this from parent component
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  // Check if barcode and catalog number are in the UI config
  const showBarcode = productFields.includes('Barcode');
  const showCatalogNumber = productFields.includes('CatalogNumber');
  const showEnhancedSearch = showBarcode || showCatalogNumber;

  const filterOptions = useCallback((options, params) => {
    const { inputValue } = params;
    const input = inputValue.toLowerCase().trim();
    
    if (!input) {
      return options.slice(0, 20);
    }
    
    if (input.length < 2) {
      return [];
    }
    
    const filtered = options.map(product => {
      const productName = (product.ProductName || '').toLowerCase();
      const productId = (product.ProductID || '').toString();
      
      // Basic search (always active)
      let matches = productName.includes(input) || productId.includes(input);
      let matchedField = null;
      
      // Enhanced search (only if fields are in UI config)
      if (showEnhancedSearch) {
        const barcode = showBarcode ? (product.Barcode || '').toLowerCase() : '';
        const catalogNumber = showCatalogNumber ? (product.CatalogNumber || '').toLowerCase() : '';
        
        // Check which field matches
        if (productName.includes(input)) {
          matchedField = productFieldsSchema.ProductName?.label || 'שם מוצר';
        } else if (showCatalogNumber && catalogNumber.includes(input)) {
          matchedField = productFieldsSchema.CatalogNumber?.label || 'מק"ט';
        } else if (showBarcode && barcode.includes(input)) {
          matchedField = productFieldsSchema.Barcode?.label || 'ברקוד';
        } else if (productId.includes(input)) {
          matchedField = 'מזהה';
        }
        
        matches = matches || 
                  (showBarcode && barcode.includes(input)) || 
                  (showCatalogNumber && catalogNumber.includes(input));
      }
      
      if (matches) {
        return showEnhancedSearch ? { ...product, matchedField } : product;
      }
      return null;
    }).filter(Boolean);
    
    return filtered.slice(0, 50);
  }, [showBarcode, showCatalogNumber, showEnhancedSearch, productFieldsSchema]);

  // Build placeholder text based on available fields
  const placeholderText = useCallback(() => {
    const fields = [`שם ${productLabel}`];
    if (showCatalogNumber) fields.push('מק"ט');
    if (showBarcode) fields.push('ברקוד');
    return `הקלד ${fields.join(', ')}`;
  }, [productLabel, showBarcode, showCatalogNumber]);

  return (
    <Autocomplete
      freeSolo
      options={products}
      getOptionLabel={(option) => 
        typeof option === 'string' ? option : option.ProductName || ''
      }
      filterOptions={filterOptions}
      disableListWrap
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={(event, newValue) => {
        if (typeof newValue === 'string') {
          onSearch(newValue);
        } else if (newValue && typeof newValue === 'object') {
          setInputValue(newValue.ProductName);
          onSearch(newValue.ProductName);
        } else {
          onSearch('');
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          onSearch(inputValue);
        }
      }}
      forcePopupIcon={false}
      renderOption={showEnhancedSearch ? (props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">
                {option.ProductName}
              </Typography>
              {option.matchedField && (
                <Chip 
                  label={option.matchedField} 
                  size="small" 
                  variant="outlined"
                  color="primary"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
              {showCatalogNumber && option.CatalogNumber && (
                <Typography variant="caption" color="text.secondary">
                  {productFieldsSchema.CatalogNumber?.label || 'מק"ט'}: {option.CatalogNumber}
                </Typography>
              )}
              {showBarcode && option.Barcode && (
                <Typography variant="caption" color="text.secondary">
                  {productFieldsSchema.Barcode?.label || 'ברקוד'}: {option.Barcode}
                </Typography>
              )}
              {option.Price !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  ₪{option.Price}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      ) : undefined} // Use default rendering if enhanced search is disabled
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={`חיפוש ${productLabel}`}
          placeholder={placeholderText()}
          size="small"
          sx={{ width: { xs: '100%', sm: 300 } }}
          helperText={inputValue.length === 1 ? "הקלד לפחות 2 תווים לחיפוש" : ""}
        />
      )}
    />
  );
});

const ImageLightbox = ({ open, onClose, image, getOptimizedImageUrl }) => {
  if (!image) return null;

  return (
    <MobileDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          margin: { xs: 0, sm: 2 },
          maxWidth: { xs: '100vw', sm: '95vw' },
          maxHeight: { xs: '100vh', sm: '95vh' },
          minHeight: { xs: '100vh', sm: '50vh' },
          minWidth: { xs: '100vw', sm: '50vw' }
        }
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 1,
          backgroundColor: 'rgba(255,255,255,0.9)',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,1)'
          }
        }}
      >
        <CloseIcon />
      </IconButton>
      
      <DialogContent 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'transparent',
          p: { xs: 0, sm: 2 },
          position: 'relative',
          minHeight: '400px'
        }}
      >
        <Box sx={{
          position: 'absolute',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}>
          <CircularProgress 
            size={60}
            thickness={2}
            sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
          />
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            טוען תמונה...
          </Typography>
        </Box>
        
        <img
          src={getOptimizedImageUrl(image.display_url || image.url)}
          alt={image.name || ''}
          style={{ 
            maxWidth: '100%',
            maxHeight: '85vh',
            objectFit: 'contain',
            display: 'block',
            opacity: 0,
            transition: 'opacity 0.3s ease-in-out'
          }}
          onLoad={(e) => {
            e.target.style.opacity = '1';
            const loader = e.target.previousSibling;
            if (loader) loader.style.display = 'none';
          }}
        />
      </DialogContent>
    </MobileDialog>
  );
};

const ProductImageThumbnail = ({ product, onClick }) => {
  const hasImages = product.images?.length > 0;
  const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
  const imageCount = product.images?.length || 0;

  if (!hasImages) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        width: 60,
        height: 60,
        backgroundColor: '#f5f5f5',
        borderRadius: 1,
        border: '1px dashed #ddd'
      }}>
        <ImageIcon sx={{ color: 'action.disabled', fontSize: 32 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <Badge
        badgeContent={imageCount > 1 ? imageCount : null}
        color="primary"
        overlap="circular"
        sx={{
          '& .MuiBadge-badge': {
            fontSize: '0.65rem',
            minWidth: '18px',
            height: '18px',
            top: 4,
            right: 4
          }
        }}
      >
        <Box
          onClick={(e) => {
            e.stopPropagation();
            onClick(primaryImage);
          }}
          sx={{
            position: 'relative',
            width: 60,
            height: 60,
            borderRadius: 1,
            overflow: 'hidden',
            border: '2px solid #1976d2',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: 3,
              '& .expand-overlay': {
                opacity: 1
              }
            }
          }}
        >
          <img
            src={primaryImage.thumbnail_url}
            alt={primaryImage.name || product.ProductName}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
          />
          
          {/* Expand overlay hint */}
          {imageCount > 1 && (
            <Box
              className="expand-overlay"
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 0.25,
                opacity: 0,
                transition: 'opacity 0.2s',
                gap: 0.5
              }}
            >
              <VisibilityIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                לחץ להגדלה
              </Typography>
            </Box>
          )}
        </Box>
      </Badge>
    </Box>
  );
};

const ProductImagesPanel = ({ product, onImageClick }) => {
  if (!product.images?.length) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
        <PhotoLibraryIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
        <Typography color="text.secondary">אין תמונות למוצר זה</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
        כל התמונות ({product.images.length})
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {product.images.map((image) => (
          <Paper
            key={image.id}
            elevation={2}
            onClick={() => onImageClick(image)}
            sx={{
              cursor: 'pointer',
              position: 'relative',
              border: image.is_primary ? '3px solid #1976d2' : '1px solid #ddd',
              borderRadius: 1,
              overflow: 'hidden',
              transition: 'all 0.3s',
              '&:hover': { 
                transform: 'scale(1.05)',
                boxShadow: 4
              }
            }}
          >
            <img
              src={image.thumbnail_url}
              alt={image.name || ''}
              style={{ 
                width: 100, 
                height: 100, 
                objectFit: 'cover',
                display: 'block'
              }}
            />
            
            {image.is_primary && (
              <Chip 
                label="ראשית" 
                size="small" 
                sx={{ 
                  position: 'absolute', 
                  top: 4, 
                  right: 4,
                  fontSize: '0.65rem',
                  backgroundColor: 'primary.main',
                  color: 'white'
                }}
              />
            )}
            
            {image.name && (
              <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: 'white',
                p: 0.5,
                fontSize: '0.7rem',
                textAlign: 'center'
              }}>
                {image.name}
              </Box>
            )}
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

const ProductsPage = () => {
  const navigate = useNavigate();
  const { userProfile } = useUser();
  const role = userProfile?.role;
  const queryClient = useQueryClient();

  // UI config
  const uiConfig = userProfile?.ui_config || {};
  const allowOrderManagement = uiConfig.allow_order_management || false;
  const orderConfig = userProfile?.group?.order_config || {};
  const storageConfig = orderConfig.storage_config || {};
  const storageEnabled = storageConfig.enabled && allowOrderManagement;
  const productFields = uiConfig.product_fields || [];
  const productFieldsSchema = uiConfig.product_fields_schema || {};
  const generalSchema = uiConfig?.orders_general_configs?.general_schema || {}; 

  const productLabel = generalSchema?.product?.label || 'מוצר';
  const productPluralLabel = generalSchema?.product?.plural_label || 'מוצרים';

  // Permission roles from config
  const pageConfig = uiConfig.pages_configs?.ProductsPage || {};
  const editProductRoles = pageConfig.edit_product_roles || ["manager", "employee"];
  const deleteProductRoles = pageConfig.delete_product_roles || ["manager", "employee"];
  const inventoryTakeRoles = pageConfig.inventory_take_roles || ["manager", "employee"];
  const inventoryAddRoles = pageConfig.inventory_add_roles || ["manager", "employee"];
  const createStorageRoles = pageConfig.create_storage_roles || ["manager", "employee"];
  const editStorageRoles = pageConfig.edit_storage_roles || ["manager", "employee"];
  const deleteStorageRoles = pageConfig.delete_storage_roles || ["manager", "employee"];
  const inventoryActionPageConfig = uiConfig.pages_configs?.InventoryActionPage || {};
  const manageKioskRoles = inventoryActionPageConfig.manage_kiosk_tokens_roles || ['manager'];

  // Permission checks
  const canEditProduct = editProductRoles.includes(role);
  const canDeleteProduct = deleteProductRoles.includes(role);
  const canTakeInventory = inventoryTakeRoles.includes(role);
  const canAddInventory = inventoryAddRoles.includes(role);
  const canUpdateInventory = canTakeInventory || canAddInventory;
  const canCreateStorage = createStorageRoles.includes(role);
  const canEditStorage = editStorageRoles.includes(role);
  const canDeleteStorage = deleteStorageRoles.includes(role);
  const canManageKioskTokens = manageKioskRoles.includes(role);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  const [ordering, setOrdering] = useState('ProductName');

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  
  // Storage management states
  const [openStorageDialog, setOpenStorageDialog] = useState(false);
  const [storageDialogMode, setStorageDialogMode] = useState('create');
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [openInventoryDialog, setOpenInventoryDialog] = useState(false);
  const [selectedProductForInventory, setSelectedProductForInventory] = useState(null);
  const [openDeleteStorageDialog, setOpenDeleteStorageDialog] = useState(false);
  const [storageToDelete, setStorageToDelete] = useState(null);
  const [selectedStorageFilter, setSelectedStorageFilter] = useState('');

  // Kiosk token management dialog state
  const [kioskTokensStorage, setKioskTokensStorage] = useState(null);

  // Barcode dialogs state
  const barcodeEnabled = userProfile?.group?.barcode_config?.enabled === true;
  const [barcodePrintDialogOpen, setBarcodePrintDialogOpen] = useState(false);
  const [generateBarcodeDialogOpen, setGenerateBarcodeDialogOpen] = useState(false);
  const [quickScanDialogOpen, setQuickScanDialogOpen] = useState(false);
  const [cameraScanDialogOpen, setCameraScanDialogOpen] = useState(false);

  // Insights panel state
  const [highlightIds, setHighlightIds] = useState({});
  const [insightFilter, setInsightFilter] = useState(null);
  const gridRef = useRef(null);
  const userId = userProfile?.user_id || userProfile?.id;

  // Inventory filter
  const INVENTORY_FILTER_KEY = 'products-inventory-filter';
  const [inventoryThreshold, setInventoryThreshold] = useState(() => {
    try {
      const saved = sessionStorage.getItem(INVENTORY_FILTER_KEY);
      return saved ? Number(saved) : null;
    } catch (e) { return null; }
  });

  const handleInventoryFilterApply = (threshold) => {
    setInventoryThreshold(threshold);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Images
  const imagesEnabled = productFields.includes('images');
  const [expandedRows, setExpandedRows] = useState([]);
  const [cloudinaryConfig, setCloudinaryConfig] = useState({ cloud_name: '' });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const theme = useTheme();
  const mediaQueryMatch = useMediaQuery(theme.breakpoints.down('xl'));
  const isPhone = useIsPhone();
  
  // Use state to ensure proper updates
  const [isSmallScreen, setIsSmallScreen] = useState(() => {
    // Initialize based on current window width
    if (typeof window !== 'undefined') {
      return window.innerWidth < theme.breakpoints.values.xl;
    }
    return mediaQueryMatch;
  });
  
  useEffect(() => {
    const fetchCloudinaryConfig = async () => {
      try {
        const response = await API.get('/image/config/');
        setCloudinaryConfig(response.data);
      } catch (error) {
        console.error('Failed to fetch Cloudinary config:', error);
      }
    };
    fetchCloudinaryConfig();
  }, []);

  // Update when media query changes
  useEffect(() => {
    setIsSmallScreen(mediaQueryMatch);
  }, [mediaQueryMatch]);
  
  // Also listen to window resize as backup
  useEffect(() => {
    const handleResize = () => {
      const isSmall = window.innerWidth < theme.breakpoints.values.xl;
      setIsSmallScreen(isSmall);
    };
    
    // Check on mount
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty deps - only run on mount/unmount
  
  // Force check on route changes
  useEffect(() => {
    // Re-evaluate when component mounts (after navigation)
    const isSmall = window.innerWidth < theme.breakpoints.values.xl;
    setIsSmallScreen(isSmall);
  }, [theme.breakpoints.values.xl]);

  // Debounce search term
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Insights panel callbacks
  const handleHighlightIdsChange = useCallback((ids) => {
    setHighlightIds(ids);
  }, []);

  const handleShowAll = useCallback((category) => {
    const insightsCache = queryClient.getQueryData(['products', 'insights']);
    if (!insightsCache) return;

    let ids = [];
    let label = '';
    if (category === 'low_stock') {
      ids = insightsCache.low_stock_ids || [];
      label = 'מלאי נמוך';
    } else if (category === 'out_of_stock') {
      ids = insightsCache.out_of_stock_ids || [];
      label = 'אזל מהמלאי';
    }

    if (ids.length > 0) {
      setInsightFilter({ category, ids, label });
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }

    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [queryClient]);

  const getRowClassName = useCallback((params) => {
    const id = params.row?.ProductID;
    if (!id) return '';
    if (insightFilter) {
      if (!insightFilter.ids?.includes(id)) return '';
      if (insightFilter.category === 'low_stock') return 'row-low-stock';
      if (insightFilter.category === 'out_of_stock') return 'row-out-of-stock';
      return '';
    }
    if (highlightIds.low_stock?.has(id)) return 'row-low-stock';
    if (highlightIds.out_of_stock?.has(id)) return 'row-out-of-stock';
    return '';
  }, [highlightIds, insightFilter]);

  useEffect(() => {
    // Only load Cloudinary gallery script if images are enabled
    if (imagesEnabled && !window.cloudinary) {
      const script = document.createElement('script');
      script.src = 'https://product-gallery.cloudinary.com/all.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, [imagesEnabled]);
  
  // Fetch paginated products
  const {
    data: paginatedData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['products', 'paginated', paginationModel.page + 1, paginationModel.pageSize, debouncedSearchTerm, ordering, selectedStorageFilter, inventoryThreshold, insightFilter?.ids?.join(',') || ''],
    queryFn: () => fetchPaginatedProducts({
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      search: debouncedSearchTerm,
      ordering: ordering,
      storageId: selectedStorageFilter,
      inventoryMin: inventoryThreshold,
      ids: insightFilter?.ids,
    }),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch autocomplete products
  const {
    data: autocompleteProducts = [],
  } = useQuery({
    queryKey: ['products', 'autocomplete'],
    queryFn: fetchAllProductsForAutocomplete,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // Fetch storages if storage management is enabled
  const {
    data: storages = [],
    isLoading: isLoadingStorages,
    refetch: refetchStorages,
  } = useQuery({
    queryKey: ['storages'],
    queryFn: fetchStorages,
    enabled: storageEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const products = paginatedData?.results || [];
  const totalRows = paginatedData?.count || 0;

  const getOptimizedImageUrl = useCallback((url) => {
    if (!url) return '';
    if (!url.includes('cloudinary.com')) return url;
    
    const transformations = 'f_auto,q_auto:best,c_limit';
    
    if (url.includes('/upload/v')) {
      return url.replace('/upload/', `/upload/${transformations}/`);
    }
    
    const uploadIndex = url.indexOf('/upload/') + 8;
    const nextSlashIndex = url.indexOf('/', uploadIndex);
    
    if (nextSlashIndex > uploadIndex) {
      return url.substring(0, uploadIndex) + transformations + url.substring(nextSlashIndex);
    }
    
    return url.replace('/upload/', `/upload/${transformations}/`);
  }, []);


  const handleImageClick = useCallback((image) => {
    setSelectedImage(image);
    setLightboxOpen(true);
  }, []);

  // Dialog handlers
  const handleDeleteClick = useCallback((product) => {
    setSelectedProduct(product);
    setOpenDeleteDialog(true);
  }, []);

  const handleInventoryClick = useCallback((product) => {
    setSelectedProductForInventory(product);
    setOpenInventoryDialog(true);
  }, []);

  const handleStorageEdit = useCallback((storage) => {
    setSelectedStorage(storage);
    setStorageDialogMode('edit');
    setOpenStorageDialog(true);
  }, []);

  const handleStorageDelete = useCallback((storage) => {
    setStorageToDelete(storage);
    setOpenDeleteStorageDialog(true);
  }, []);

  // Build columns
  const columns = useMemo(() => {
    const cols = [];
    
    // Get product_fields from UI config
    const productFields = uiConfig.product_fields || [];
    const productFieldsSchema = uiConfig.product_fields_schema || {};
    
    // Helper to check if field is in productFields
    const isFieldIncluded = (fieldName) => productFields.includes(fieldName);
    
    // Fields to always exclude from UI display
    const excludedFields = ['CreatedAt', 'IsDeleted', 'DeletedAt', 'ProductID', 'UserGroup', 'images', 'primary_image_thumbnail'];
    
    if (imagesEnabled) {
      cols.push({
        field: 'image',
        headerName: 'תמונה',
        width: 100,
        minWidth: 100,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        mobile: false,
        renderCell: (params) => (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            height: '100%',
            py: 1
          }}>
            <ProductImageThumbnail
              product={params.row}
              onClick={handleImageClick}
            />
          </Box>
        )
      });
    }
  
    // 1. ProductName - Always first
    cols.push({
      field: 'ProductName',
      headerName: productFieldsSchema.ProductName?.label || `שם ${productLabel}`,
      flex: 2,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        canEditProduct ? (
          <Button
            variant="text"
            color="primary"
            onClick={() => navigate(`/product/${params.row.ProductID}`)}
            sx={{ textDecoration: 'underline', cursor: 'pointer' }}
          >
            {params.value}
          </Button>
        ) : (
          <Typography variant="body2">
            {params.value}
          </Typography>
        )
      ),
    });

    // 2. CatalogNumber if in product_fields
    if (isFieldIncluded('CatalogNumber') && !excludedFields.includes('CatalogNumber')) {
      cols.push({
        field: 'CatalogNumber',
        headerName: productFieldsSchema.CatalogNumber?.label || 'מק"ט',
        flex: 1,
        minWidth: 120,
        align: 'center',
        headerAlign: 'center',
        mobile: false,
        renderCell: (params) => params.value || '–',
      });
    }

    // 3. Barcode if in product_fields
    if (isFieldIncluded('Barcode') && !excludedFields.includes('Barcode')) {
      cols.push({
        field: 'Barcode',
        headerName: productFieldsSchema.Barcode?.label || 'ברקוד',
        flex: 1,
        minWidth: 150,
        align: 'center',
        headerAlign: 'center',
        mobile: false,
        renderCell: (params) => (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', direction: 'ltr', lineHeight: 1 }}>
              {params.value || '–'}
            </Typography>
          </Box>
        ),
      });
    }

    // 4. Price - Always shown
    cols.push({
      field: 'Price',
      headerName: productFieldsSchema.Price?.label || 'מחיר',
      flex: 1,
      minWidth: 130,  
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (params.value === undefined || params.value === null) return '';
        return new Intl.NumberFormat('he-IL', {
          style: 'currency',
          currency: 'ILS',
        }).format(params.value);
      },
    });

    // 5. unit if in product_fields
    if (isFieldIncluded('unit') && !excludedFields.includes('unit')) {
      cols.push({
        field: 'unit',
        headerName: productFieldsSchema.unit?.label || 'יחידה',
        flex: 0.8,
        minWidth: 100,
        align: 'center',
        headerAlign: 'center',
        mobile: false,
        renderCell: (params) => params.value || '–',
      });
    }

    // 6. Inventory fields if storage enabled
    if (storageEnabled) {
      // Per-storage columns (one column per storage, before total)
      if (storages && storages.length > 0) {
        storages.forEach((storage) => {
          cols.push({
            field: `storage_${storage.StorageID}`,
            headerName: storage.StorageName,
            flex: 0.7,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderHeader: () => (
              <Tooltip title={storage.StorageName} placement="top" arrow>
                <Typography sx={{
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  textAlign: 'center',
                }}>
                  {storage.StorageName}
                </Typography>
              </Tooltip>
            ),
            valueGetter: (value, row) => {
              const inv = (row.inventory || []).find(i => i.StorageID === storage.StorageID);
              return inv ? inv.available_quantity : 0;
            },
            renderCell: (params) => {
              const qty = params.value || 0;
              return (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}>
                  <Typography sx={{
                    fontWeight: qty > 0 ? 'bold' : 400,
                    color: qty > 0 ? 'text.primary' : 'text.disabled',
                    fontSize: isPhone ? '0.85rem' : '1.1rem',
                  }}>
                    {qty}
                  </Typography>
                </Box>
              );
            },
          });
        });
      }

      // Total inventory column (after per-storage breakdown)
      cols.push({
        field: 'total_quantity',
        headerName: 'סה"כ מלאי',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      valueGetter: (value, row) => {
        if (row.total_quantity !== undefined) {
          return row.total_quantity;
        }
        const totalQuantity = row.inventory ? 
          row.inventory.reduce((sum, inv) => sum + (inv.available_quantity || 0), 0) : 0;
        return totalQuantity;
      },
      renderCell: (params) => {
        const totalQuantity = params.value || 0;
        
        return (
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }}>
            <Paper
              elevation={0}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: isPhone ? 0.3 : 2,  // NEW - smaller padding
                py: isPhone ? 0.1 : 0.5,  // NEW - smaller padding
                backgroundColor: totalQuantity > 0 ? '#e8f5e9' : '#ffebee',
                border: isPhone ? '1px solid' : '2px solid',  // NEW - thinner border
                borderColor: totalQuantity > 0 ? '#4caf50' : '#f44336',
                borderRadius: isPhone ? 0.75 : 2,  // NEW - smaller radius
              }}
            >
              <Typography 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: isPhone ? '0.65rem' : '1.1rem',  // NEW - smaller font
                  color: totalQuantity > 0 ? '#2e7d32' : '#c62828',
                  lineHeight: 1,
                }}
              >
                {totalQuantity}
              </Typography>
            </Paper>
          </Box>
        );
      },
    });

    // Note: the combined "פירוט מלאי" column was removed because each
    // storage already has its own dedicated column above (added in the
    // storages.forEach loop). Showing the breakdown twice was redundant
    // and made the grid wider than it needed to be.
  }


    // 7. All other fields from product_fields (excluding already added and excluded fields)
    const alreadyAddedFields = [
      'ProductName', 'CatalogNumber', 'Barcode', 'Price', 'unit', 
      'inventory', 'totalInventory', 'Description', 'actions'
    ];

    productFields.forEach(fieldName => {
      // Skip if already added or in excluded list
      if (!alreadyAddedFields.includes(fieldName) && !excludedFields.includes(fieldName)) {
        const fieldSchema = productFieldsSchema[fieldName] || {};
        const column = {
          field: fieldName,
          headerName: fieldSchema.label || fieldName,
          flex: 1,
          minWidth: 100,
          align: 'center',
          headerAlign: 'center',
          renderCell: (params) => {
            const value = params.value;
            
            // Handle different field types
            if (fieldSchema.type === 'date' || fieldName.includes('Date') || fieldName.includes('At')) {
              if (!value) return '–';
              return new Date(value).toLocaleDateString('he-IL');
            }
            
            if (fieldSchema.type === 'number') {
              if (value === undefined || value === null) return '–';
              return new Intl.NumberFormat('he-IL').format(value);
            }
            
            if (fieldSchema.type === 'boolean') {
              return value ? 'כן' : 'לא';
            }
            
            // Default string handling with tooltip
            return (
              <Tooltip 
                title={value || '–'}
                arrow 
                placement="top"
                enterDelay={500}
              >
                <div style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  width: '100%',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}>
                  {value || '–'}
                </div>
              </Tooltip>
            );
          },
        };
        cols.push(column);
      }
    });

    // 8. Description if in product_fields (or always show it by default)
    if ((isFieldIncluded('Description') || !productFields.length) && !excludedFields.includes('Description')) {
      cols.push({
        field: 'Description',
        headerName: productFieldsSchema.Description?.label || 'תיאור',
        flex: isSmallScreen ? 1 : 3,
        maxWidth: 120,
        headerAlign: 'center',
        renderCell: (params) => (
          <Tooltip 
            title={params.value || 'אין תיאור'}
            arrow 
            placement="top"
            enterDelay={500}
          >
            <div style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              width: '100%',
              cursor: 'pointer',
              textAlign: 'center'
            }}>
              {params.value || 'אין תיאור'}
            </div>
          </Tooltip>
        ),
      });
    }

    // 9. Actions - Always last
    cols.push({
      field: 'actions',
      headerName: 'פעולות',
      flex: 2,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => {
        return (
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }}>
            <Box sx={{
              display: 'flex',
              gap: isSmallScreen ? 0.3 : 1,  // Adjusted
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              {canEditProduct && (
                <Tooltip title={`ערוך ${productLabel}`} arrow placement="top">
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={() => navigate(`/product/${params.row.ProductID}`)}
                    sx={{
                      minWidth: isSmallScreen ? 28 : 'auto',  // Adjusted
                      padding: isSmallScreen ? '3px' : '6px',  // Adjusted
                      height: isSmallScreen ? 28 : 32,  // Adjusted
                      '& .MuiButton-startIcon': {
                        margin: 0
                      }
                    }}
                  >
                    <EditIcon sx={{ fontSize: isSmallScreen ? 16 : 20 }} />  {/* Adjusted */}
                  </Button>
                </Tooltip>
              )}
              {storageEnabled && canUpdateInventory && (
                <Tooltip title="ניהול מלאי" arrow placement="top">
                  <Button
                    variant="outlined"
                    size="small"
                    color="info"
                    onClick={() => handleInventoryClick(params.row)}
                    sx={{
                      minWidth: isSmallScreen ? 28 : 'auto',
                      padding: isSmallScreen ? '3px' : '6px',
                      height: isSmallScreen ? 28 : 32,
                      '& .MuiButton-startIcon': {
                        margin: 0
                      }
                    }}
                  >
                    <InventoryIcon sx={{ fontSize: isSmallScreen ? 16 : 20 }} />
                  </Button>
                </Tooltip>
              )}
              {canDeleteProduct && (
                <Tooltip title={`מחק ${productLabel}`} arrow placement="top">
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(params.row)}
                    sx={{
                      minWidth: isSmallScreen ? 28 : 'auto',
                      padding: isSmallScreen ? '3px' : '6px',
                      height: isSmallScreen ? 28 : 32,
                      '& .MuiButton-startIcon': {
                        margin: 0
                      }
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: isSmallScreen ? 16 : 20 }} />
                  </Button>
                </Tooltip>
              )}
            </Box>
          </Box>
        );
      },
    });

    return cols;
  }, [uiConfig, productLabel, navigate, handleDeleteClick, handleInventoryClick, storageEnabled, storages, isSmallScreen, isPhone, imagesEnabled, canEditProduct, canDeleteProduct, canUpdateInventory]);

  const handleDialogSuccess = useCallback(() => {
    queryClient.invalidateQueries(['products']);
    queryClient.invalidateQueries(['storages']);
    setOpenCreateDialog(false);
    setOpenDeleteDialog(false);
    setOpenStorageDialog(false);
    setOpenInventoryDialog(false);
    setOpenDeleteStorageDialog(false);
    setSelectedProduct(null);
    setSelectedStorage(null);
    setSelectedProductForInventory(null);
    setStorageToDelete(null);
  }, [queryClient]);

  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const NoRowsOverlay = React.memo(() => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
      אין {productPluralLabel} להצגה
    </Box>
  ));

  // ==================== Mobile Card Renderer ====================
  const productCardRenderer = useCallback((row) => {
    const price = parseFloat(row?.Price || '0');
    const formatted = new Intl.NumberFormat('he-IL', {
      style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(price);

    const thumbnailUrl = imagesEnabled && row?.primary_image_thumbnail
      ? getOptimizedImageUrl(row.primary_image_thumbnail)
      : null;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {imagesEnabled && (
          <Box
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              borderRadius: 1,
              overflow: 'hidden',
              backgroundColor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={row.ProductName}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <ImageIcon sx={{ color: 'grey.400', fontSize: 24 }} />
            )}
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.ProductName}
          </Typography>
          {row.CatalogNumber && (
            <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              מק"ט: {row.CatalogNumber}
            </Typography>
          )}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flexShrink: 0, ml: 1 }}>
          {formatted}
        </Typography>
      </Box>
    );
  }, [imagesEnabled, getOptimizedImageUrl]);

  return (
    <ThemeProvider theme={theme}>
      <div dir="rtl">
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 1, sm: 2 } }}>
          <Typography variant="h3" sx={{ mb: 3 }}>
            ניהול {productPluralLabel}
          </Typography>

          {/* Storage Management Section - Only shown if enabled */}
          {storageEnabled && (
            <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              ניהול מחסנים
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {canCreateStorage && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSelectedStorage(null);
                    setStorageDialogMode('create');
                    setOpenStorageDialog(true);
                  }}
                  sx={{direction: 'ltr'}}
                >
                  הוסף מחסן
                </Button>
              )}
            </Box>
            </Box>
              <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 3, p: 0, overflow: 'hidden' }}>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                        רשימת מחסנים
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    py: 1.5,
                  }}
                />
                <CardContent>
                  {isLoadingStorages ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <CircularProgress />
                    </Box>
                  ) : storages.length === 0 ? (
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      אין מחסנים. הוסף מחסן ראשון כדי להתחיל לנהל מלאי.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>שם מחסן</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>תיאור</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>כתובת</TableCell>
                            <TableCell align="center">מוצרים</TableCell>
                            <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>כמות כוללת</TableCell>
                            <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>סטטוס</TableCell>
                            <TableCell align="center">פעולות</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {storages.map((storage) => (
                            <TableRow
                              key={storage.StorageID}
                              sx={{
                                backgroundColor: 'white',
                                '&:hover': {
                                  backgroundColor: '#f5f5f5',
                                },
                              }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {storage.StorageName}
                                  {storage.is_default && (
                                    <CheckCircleIcon color="primary" fontSize="small" />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{storage.Description || '-'}</TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{storage.Address || '-'}</TableCell>
                              <TableCell align="center">{storage.inventory_count || 0}</TableCell>
                              <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{storage.total_items || 0}</TableCell>
                              <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                {storage.is_default ? (
                                  <Chip size="small" label="ברירת מחדל" color="primary" />
                                ) : (
                                  <Chip size="small" label="רגיל" variant="outlined" />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                  {canEditStorage && (
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleStorageEdit(storage)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                  {canManageKioskTokens && (
                                    <Tooltip title="צור קישור לעמדת מלאי">
                                      <IconButton
                                        size="small"
                                        color="secondary"
                                        onClick={() => setKioskTokensStorage(storage)}
                                      >
                                        <PointOfSaleIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {canDeleteStorage && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleStorageDelete(storage)}
                                      disabled={storages.length === 1}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
              
              {/* Separator between sections */}
              <Divider sx={{ my: 3 }} />
            </>
          )}

          {/* Product Insights Panel - Only shown if storage/inventory enabled */}
          {storageEnabled && (
            <ProductInsightsPanel
              userId={userId}
              isManager={role === 'manager'}
              onHighlightIdsChange={handleHighlightIdsChange}
              onShowAll={handleShowAll}
            />
          )}

          {/* Insight filter indicator */}
          {insightFilter && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Chip
                label={`מסנן: ${insightFilter.label} (${insightFilter.ids.length})`}
                onDelete={() => setInsightFilter(null)}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}

          {/* Products Section */}
          <Card ref={gridRef} sx={{ borderRadius: 2, boxShadow: 3, p: 0, overflow: 'hidden' }}>
            <CardHeader
              title={
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                  רשימת {productPluralLabel}
                </Typography>
              }
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                py: 1.5,
              }}
            />
            <CardContent>
              {/* Search & Add button row */}
              <ResponsiveToolbar>
                {/*
                  Autocomplete + camera-scan IconButton share a single
                  flex row so they count as ONE child of ResponsiveToolbar.
                  Claim both grid columns on mobile (gridColumn: '1 / -1')
                  so the search bar stays full-width — ResponsiveToolbar's
                  default rule that grants full width only hits the direct
                  .MuiAutocomplete-root child, which is one level deeper
                  now that we wrap.
                */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    minWidth: 0,
                    width: '100%',
                    gridColumn: { xs: '1 / -1', md: 'auto' },
                    flex: { md: 1 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ProductAutocomplete
                      products={autocompleteProducts}
                      productLabel={productLabel}
                      onSearch={handleSearch}
                      productFields={productFields}  // Pass from your UI config
                      productFieldsSchema={productFieldsSchema}  // Pass from your UI config
                    />
                  </Box>
                  {barcodeEnabled && (
                    <Tooltip title="חפש לפי סריקת ברקוד">
                      <IconButton
                        onClick={() => setCameraScanDialogOpen(true)}
                        sx={{
                          bgcolor: 'action.hover',
                          '&:hover': { bgcolor: 'action.selected' },
                          flexShrink: 0,
                        }}
                        aria-label="חפש לפי סריקת ברקוד"
                      >
                        <QrCodeScannerIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {storageEnabled && storages.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 150 } }}>
                    <InputLabel>סינון לפי מחסן</InputLabel>
                    <Select
                      value={selectedStorageFilter}
                      onChange={(e) => {
                        setSelectedStorageFilter(e.target.value);
                        setPaginationModel(prev => ({ ...prev, page: 0 }));
                      }}
                      label="סינון לפי מחסן"
                      endAdornment={
                        selectedStorageFilter && (
                          <InputAdornment position="end" sx={{ mr: 2 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedStorageFilter('');
                                setPaginationModel(prev => ({ ...prev, page: 0 }));
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                    >
                      <MenuItem value="">הכל</MenuItem>
                      {storages.map((storage) => (
                        <MenuItem key={storage.StorageID} value={storage.StorageID}>
                          {storage.StorageName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {storageEnabled && (
                  <Box sx={{ position: 'relative' }}>
                    <InventoryFilterPanel
                      onApply={handleInventoryFilterApply}
                      appliedThreshold={inventoryThreshold}
                      storageKey={INVENTORY_FILTER_KEY}
                    />
                  </Box>
                )}
                {canEditProduct && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenCreateDialog(true)}
                    sx={{direction: 'ltr'}}
                  >
                    הוסף {productLabel} חדש
                  </Button>
                )}
                {barcodeEnabled && (
                  <>
                    {/*
                      Label printing and bulk barcode generation are
                      desktop-only workflows — they open dialogs with
                      multi-step pickers that don't fit a phone and are
                      rarely needed on the go. Keep the functionality
                      accessible on md+ but hide the buttons on xs/sm
                      so the toolbar isn't cluttered on mobile.
                    */}
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<LabelIcon />}
                      onClick={() => setBarcodePrintDialogOpen(true)}
                      sx={{ direction: 'ltr', display: { xs: 'none', md: 'inline-flex' } }}
                    >
                      הדפס מדבקות
                    </Button>
                    {canEditProduct && (
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<AutoFixHighIcon />}
                        onClick={() => setGenerateBarcodeDialogOpen(true)}
                        sx={{ direction: 'ltr', display: { xs: 'none', md: 'inline-flex' } }}
                      >
                        צור ברקודים
                      </Button>
                    )}
                  </>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PrintIcon />}
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
                    if (selectedStorageFilter) params.append('storage_id', selectedStorageFilter);
                    if (ordering) params.append('ordering', ordering);
                    if (inventoryThreshold != null) params.append('inventory_min', inventoryThreshold);
                    navigate(`/products/print?${params.toString()}`);
                  }}
                  sx={{direction: 'ltr'}}
                >
                  הדפסה
                </Button>
              </ResponsiveToolbar>

              {/* Products DataGrid */}
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                  <Box sx={{
                    width: '100%',
                    '.MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                      fontSize: '1.15rem',
                      whiteSpace: 'normal !important',  // ADD THIS - allows header text to wrap
                      lineHeight: '1.2',  
                      overflow: 'visible', 
                    },
                    '.MuiDataGrid-cell': {
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      fontSize: '1rem',
                    },
                  }}>
                  <ResponsiveDataView
                    rows={products}
                    columns={columns}
                    getRowId={(row) => row.ProductID}
                    cardRenderer={productCardRenderer}
                    onRowClick={(row) => navigate(`/product/${row.ProductID}`)}
                    emptyMessage="אין תוצאות"
                    density="comfortable"
                    autoHeight
                    pagination
                    rowCount={totalRows}
                    pageSizeOptions={[25, 50, 100]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    paginationMode="server"
                    sortingMode="server"  
                    onSortModelChange={(model) => {
                      if (model.length > 0) {
                        const sortField = model[0].field;
                        const sortOrder = model[0].sort === 'desc' ? '-' : '';
                        
                        const fieldMapping = {
                          'total_quantity': 'total_inventory',
                        };
                        
                        const backendField = fieldMapping[sortField] || sortField;
                        const newOrdering = `${sortOrder}${backendField}`;
                        
                        setOrdering(newOrdering);  // Update state
                        setPaginationModel(prev => ({ ...prev, page: 0 }));  // Reset to first page
                      } else {
                        setOrdering('ProductName');  // Reset to default
                      }
                    }}
                    {...(imagesEnabled && cloudinaryConfig ? {
                      getDetailPanelContent: ({ row }) => 
                        <ProductImagesPanel 
                          product={row} 
                          onImageClick={handleImageClick}
                        />,
                      getDetailPanelHeight: () => 'auto',
                      detailPanelExpandedRowIds: expandedRows,
                      onDetailPanelExpandedRowIdsChange: setExpandedRows
                    } : {})}
                    disableSelectionOnClick
                    getRowClassName={storageEnabled ? getRowClassName : undefined}
                    localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                    rowHeight={60}
                    columnBuffer={2}
                    rowBuffer={10}
                    rowThreshold={10}
                    slots={{
                      toolbar: CustomToolbar,
                      noRowsOverlay: NoRowsOverlay,
                    }}
                    sx={{
                      // '.MuiDataGrid-columnHeaderTitle': {
                      //   fontWeight: 'bold',
                      //   fontSize: '1.15rem',
                      // },
                      // '.MuiDataGrid-cell': {
                      //   whiteSpace: 'normal',
                      //   wordBreak: 'break-word',
                      //   fontSize: '1rem',
                      // },
                      // '& .MuiDataGrid-virtualScroller': {
                      //   overflowX: 'auto !important',
                      // },
                      '& .row-low-stock': {
                        backgroundColor: '#fff3e0',
                      },
                      '& .row-out-of-stock': {
                        backgroundColor: '#ffebee',
                      },
                      ...(imagesEnabled ? {
                        '& .MuiDataGrid-detailPanel': {
                          backgroundColor: '#f5f5f5',
                          padding: 0
                        },
                        '& .MuiDataGrid-detailPanelContainer': {
                          backgroundColor: '#f5f5f5'
                        }
                      } : {})
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Dialogs */}
          <CreateProductDialog
            open={openCreateDialog}
            onClose={() => setOpenCreateDialog(false)}
            productFieldsSchema={productFieldsSchema}
            productLabel={productLabel}
            onSuccess={handleDialogSuccess}
            productFields={productFields}
            storageEnabled={storageEnabled}
            storages={storages}
            barcodeEnabled={barcodeEnabled}
          />

          <DeleteProductDialog
            open={openDeleteDialog}
            onClose={() => {
              setOpenDeleteDialog(false);
              setSelectedProduct(null);
            }}
            product={selectedProduct}
            productLabel={productLabel}
            onSuccess={handleDialogSuccess}
          />

          {storageEnabled && (
            <>
              <StorageManagementDialog
                open={openStorageDialog}
                onClose={() => {
                  setOpenStorageDialog(false);
                  setSelectedStorage(null);
                }}
                storage={selectedStorage}
                mode={storageDialogMode}
                onSuccess={handleDialogSuccess}
              />

              <InventoryManagementDialog
                open={openInventoryDialog}
                onClose={() => {
                  setOpenInventoryDialog(false);
                  setSelectedProductForInventory(null);
                }}
                product={selectedProductForInventory}
                storages={storages}
                onSuccess={handleDialogSuccess}
                canTakeInventory={canTakeInventory}
                canAddInventory={canAddInventory}
              />

              <KioskTokensDialog
                open={!!kioskTokensStorage}
                onClose={() => setKioskTokensStorage(null)}
                storage={kioskTokensStorage}
              />

              {barcodeEnabled && (
                <>
                  <BarcodePrintDialog
                    open={barcodePrintDialogOpen}
                    onClose={() => setBarcodePrintDialogOpen(false)}
                  />
                  <GenerateBarcodeDialog
                    open={generateBarcodeDialogOpen}
                    onClose={() => setGenerateBarcodeDialogOpen(false)}
                    products={products?.results || products || []}
                    onGenerated={() => {
                      queryClient.invalidateQueries(['products']);
                    }}
                  />
                  <BarcodeScannerDialog
                    open={cameraScanDialogOpen}
                    onClose={() => setCameraScanDialogOpen(false)}
                    productLabel={productLabel}
                    onScanText={(text) => {
                      handleSearch(text);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                  />
                </>
              )}
              
              <DeleteStorageDialog
                open={openDeleteStorageDialog}
                onClose={() => {
                  setOpenDeleteStorageDialog(false);
                  setStorageToDelete(null);
                }}
                storage={storageToDelete}
                onSuccess={handleDialogSuccess}
              />
            </>
          )}
  
          {/* Image Lightbox - Add this before other dialogs */}
          {imagesEnabled && (
            <ImageLightbox
              open={lightboxOpen}
              onClose={() => {
                setLightboxOpen(false);
                setSelectedImage(null);
              }}
              image={selectedImage}
              getOptimizedImageUrl={getOptimizedImageUrl}
            />
          )}
        </Container>
      </div>
    </ThemeProvider>
  );
};

export default ProductsPage;